import PropTypes from 'prop-types';
import React     from 'react';

import * as S            from './change-plan-bm.module.scss';
import { CurrentPlanBm } from './current-plan-bm';
import { ModalWrapper }  from '../../modal-wrapper';
import { Spinner }       from '../../../common/spinner/spinner';
import { Message }       from '../../../common/message';
import { Price }         from '../../../common/product/price';
import { Quota }         from '../../../common/product/quota';
import * as Actions      from '../../../../context/ctx-actions';
import { getUserHasAch } from '../../../../context/selectors/user-selectors';
import { useGlobalCtx }  from '../../../../context/ctx-hook';
import * as BMSelectors  from '../../../../context/selectors/bill-selectors';
import * as Tealium      from '../../../../helpers/tealium';
import { BM_ACTIONS }    from '../../../../helpers/constants/constants';
import { MODALS }        from '../../../../helpers/constants/state-constants';
import { getSkusToHide } from '../../../../helpers/bmpromo-helpers';
import * as NetworkCalls from '../../../../network/network-calls';
// import { US_SKUS }       from '../../../logged-in-template/get-site-data';

const setBPOrder = (planArr) =>
  planArr.sort((a, b) =>
    a.level === b.level
      ? 0
      : a.level < b.level ? -1 : 1
  ).map(({uriName}) => uriName);


const getBoxText = (plan, current, renewal, action) => {
  if (!current && !renewal) return 'Buy';
  if (['extra', 'standard'].includes(renewal)) return 'Buy';
  else if (plan === current) return 'Current Plan';
  else if (plan === renewal) return 'Renewal Plan';
  else if (action === BM_ACTIONS.UPGRADE) return 'Upgrade';
  else return 'Downgrade';
};

const getAction = (order, plan, current) => {
  if (current == 'standard') {
    return BM_ACTIONS.CREATE;
  } else {
    return (order.indexOf(plan) > order.indexOf(current) ? BM_ACTIONS.UPGRADE : BM_ACTIONS.DOWNGRADE);
  }
};

const PRODUCT = 'billmanager';

const ChangePlanBm = ({onClose}) => {
  const {globalDispatch, state} = useGlobalCtx();

  const HAS_ACH_PAYMENT     = getUserHasAch(state);
  const {current, renewal}  = BMSelectors.getBMPlan(state);
  const {order = [], plans} = state.billManager;

  const handleSelection = async (plan, action) => {
    try {
      globalDispatch(Actions.setCtxField('isDisabled', true));

      if (HAS_ACH_PAYMENT) {
        const {data} = await NetworkCalls.callPostChangeTier(plan, action);
        if ([BM_ACTIONS.CREATE, BM_ACTIONS.UPGRADE].includes(action)) {
          Tealium.tealiumLink(Tealium.BMTealiumEvent(state, data));
          Tealium.gtmLink(Tealium.BMTealiumEvent(state, data));
        }

        globalDispatch(Actions.mergeSubscriptionData(PRODUCT, data.postState));

        onClose();
      } else {
        globalDispatch(Actions.mergeMainStateObj({
          isDisabled: false,
          modal:      {
            type:       MODALS.UPDATE_PAYMENT,
            ctx:        {product: PRODUCT, plan, paymentOnly: false},
            isLoading:  true,
            hasError:   false,
            isDisabled: false
          }
        }));
      }
    } catch (err) {
      console.log(err);
      globalDispatch(Actions.mergeMainStateObj({
        isDisabled: false,
        modal:      {isLoading: false, hasError: true, isDisabled: false}
      }));
    }
  };

  const initChangeTierPage = React.useCallback(async () => {
    if (order.length > 0) return globalDispatch(Actions.mergeMainStateObj({modal: {isLoading: false}}));

    try {
      globalDispatch(Actions.mergeMainStateObj({modal: {isLoading: true}}));
      const {data} = await NetworkCalls.callGetChangeTiers('billmanager');
      const hideBmSkus = getSkusToHide();

      // Check to make sure that the BM sku's returned from the API are SKU's that we handle for in MyAcct
      const filteredSkus = data.filter(skuObj => !hideBmSkus.includes(skuObj?.sku));

      globalDispatch(Actions.mergeMainStateObj({
        modal:       {isLoading: false},
        billManager: {
          plans: filteredSkus.reduce((acm, cur) => ({...acm, [cur.uriName]: cur}), {}),
          order: setBPOrder(filteredSkus)
        }
      }));
    } catch (err) {
      console.log(err);
      globalDispatch(Actions.mergeMainStateObj({
        modal: {
          isLoading:  false,
          hasError:   true,
          isDisabled: false
        }
      }));
    }
  }, [order]);

  React.useEffect(() => {
    initChangeTierPage();
  }, [initChangeTierPage]);

  if (state.modal.isLoading) {
    return (
      <ModalWrapper heading="Change Plan" onClose={onClose}>
        <Spinner className={S.spinner}/>
      </ModalWrapper>
    );
  } else if (state.modal.hasError) {
    return (
      <ModalWrapper heading="Change Plan" onClose={onClose}>
        <Message
          type="ERROR"
          align="CENTER"
          messages={['Server error please try again later']}
        />
      </ModalWrapper>
    );
  } else {
    const CURRENT_INDEX  = order.indexOf(renewal ?? current ?? null);
    const CURRENT_ACTION = order.indexOf(current) < CURRENT_INDEX ? BM_ACTIONS.DOWNGRADE : BM_ACTIONS.UPGRADE;
    const BM_TYPE        = BMSelectors.getBillManagerType(state);
    const IS_STANDARD    = BMSelectors.getBMHasEntitlement(state, ['BILLPAY_STANDARD']);

    return (
      <ModalWrapper heading="Change Plan" onClose={onClose} className={S.mainWrapper}>
        <div className={S.modalBody}>
          {BM_TYPE === 'PAID' && (
            <CurrentPlanBm
              name={plans[current].name}
              sku={plans[current].sku}
              quota={state.skus[plans[current].sku].Quota}
              uriName={current}
              ctaText={getBoxText(current, current, renewal, CURRENT_ACTION)}
              handleChangePlan={() => handleSelection(current, order.indexOf(current) > CURRENT_INDEX ? 'UPGRADE' : 'DOWNGRADE')}
            />
          )}

          {BM_TYPE === 'FREE' && (
            <CurrentPlanBm
              name={IS_STANDARD ? 'Bill Manager Standard' : 'Bill Manager Extra'}
              sku={null}
              quota={`checkpay=${IS_STANDARD ? 5 : 9}|billpay=12`}
              uriName={current}
              ctaText={getBoxText(current, current, renewal, CURRENT_ACTION)}
              handleChangePlan={() => handleSelection(current, BM_ACTIONS.CREATE)}
            />
          )}


          <div className={S.label}>
            {BM_TYPE === 'NONE' ? 'Bill Manager Plans' : 'Other Options'}
          </div>

          <div className={S.optionArea}>
            {order.map((plan, i) => {
              if (plan === current) return null;
              const {sku, name, uriName} = plans[plan];
              const ACTION = getAction(order, plan, current);

              return (
                <article key={i} className={`${S.option} ${uriName}`} data-testid={`PLAN_${uriName.toUpperCase()}`}>
                  {name === 'Plus' &&
                    <div className={S.name, S.mostPopContainer}>{name}
                      <span className={S.mostPopularTag}> Most Popular</span>
                    </div>
                  }
                  {name !== 'Plus' &&
                    <div className={S.name}>{name}
                    </div>
                  }
                  
                  <Quota sku={sku} className={S.quota}/>
                  <Price
                    renewalFrequency="P1M"
                    className={S.optionPrice}
                    sku={sku}
                  />

                  <button
                    className={S.contentCTABtn}
                    disabled={plan === renewal}
                    onClick={() => handleSelection(plan, ACTION)}
                  >{getBoxText(plan, current, renewal, ACTION)}</button>
                </article>
              );
            })}
          </div>
        </div>
      </ModalWrapper>
    );
  }
};

ChangePlanBm.displayName = 'ChangePlanBm';
ChangePlanBm.propTypes   = {
  onClose: PropTypes.func.isRequired
};

export { ChangePlanBm };
